import { useState, useEffect, useMemo } from 'react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SortableTableHeader } from '../ui/SortableTableHeader';
import { useTableSort } from '../../hooks/useTableSort';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import type { AlertType } from '../../hooks/useDashboardAlerts';
import { ALERT_TYPES } from '../../hooks/useDashboardAlerts';
import type { WorkOrder, Client, ServiceLog, RouteAlert, AgingClient, MissingDataClient, SuspiciousDataClient, SuspiciousLocationAlert, PendingInventoryReview } from '../../lib/api';
import { SuspiciousLocationsTable } from './SuspiciousLocationsTable';

type AlertItemsData =
  | { type: 'workorders'; items: WorkOrder[] }
  | { type: 'clients'; items: Client[] }
  | { type: 'service-logs'; items: ServiceLog[] }
  | { type: 'routes'; items: RouteAlert[] }
  | { type: 'aging-clients'; items: AgingClient[] }
  | { type: 'missing-data-clients'; items: MissingDataClient[] }
  | { type: 'suspicious-data-clients'; items: SuspiciousDataClient[] }
  | { type: 'suspicious-locations'; items: SuspiciousLocationAlert[] }
  | { type: 'inventory-review'; items: PendingInventoryReview[] }
  | { type: 'empty'; items: never[] }
  | null;

// Action types for inline actions
export interface AlertItemActions {
  onEdit?: (item: WorkOrder | Client) => void;
  onStatusChange?: (item: WorkOrder, newStatus: string) => void;
  onAssign?: (item: WorkOrder) => void;
  onAddNote?: (item: WorkOrder | Client | ServiceLog) => void;
  // Alert dismiss/fix actions
  onDismissAlert?: (item: MissingDataClient | SuspiciousDataClient | AgingClient, alertType: string) => void;
  onFixMissingData?: (item: MissingDataClient) => void;
  onFixLocation?: (item: SuspiciousDataClient) => void;
  onFixInterval?: (item: SuspiciousDataClient) => void;
  onDismissLocationAlert?: (alert: SuspiciousLocationAlert) => void;
  // Undo dismissal action (F4 Phase 2)
  onUndoDismissal?: (dismissalId: number) => void;
}

interface AlertItemsTableProps {
  alertType: AlertType | null;
  data: AlertItemsData;
  isLoading: boolean;
  onItemClick?: (item: WorkOrder | Client | ServiceLog | AgingClient | MissingDataClient | SuspiciousDataClient | PendingInventoryReview) => void;
  actions?: AlertItemActions;
}

export function AlertItemsTable({ alertType, data, isLoading, onItemClick, actions }: AlertItemsTableProps) {
  if (!alertType) {
    return (
      <div className="flex-1 flex items-center justify-center bg-sage-50 rounded-2xl border border-sage-200">
        <div className="text-center p-8">
          <span className="text-4xl mb-4 block">📊</span>
          <p className="text-sage-600">Select an alert category to view items</p>
        </div>
      </div>
    );
  }

  const alertInfo = ALERT_TYPES[alertType];

  if (isLoading) {
    return (
      <div className="flex-1 bg-white rounded-2xl border border-sage-200 overflow-hidden">
        <TableHeader title={alertInfo.label} description={alertInfo.description} />
        <div className="p-8">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-sage-100 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.type === 'empty' || data.items.length === 0) {
    return (
      <div className="flex-1 bg-white rounded-2xl border border-sage-200 overflow-hidden">
        <TableHeader title={alertInfo.label} description={alertInfo.description} />
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <span className="text-4xl mb-4 block">✅</span>
            <p className="text-sage-600 font-medium">All clear!</p>
            <p className="text-sage-500 text-sm mt-1">No items require attention</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-white rounded-2xl border border-sage-200 overflow-hidden flex flex-col">
      <TableHeader
        title={alertInfo.label}
        description={alertInfo.description}
        count={data.items.length}
      />

      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <div className="min-w-[600px]">
          {data.type === 'workorders' && (
            <WorkordersTable items={data.items} onItemClick={onItemClick} actions={actions} alertType={alertType} />
          )}
          {data.type === 'clients' && (
            <ClientsTable items={data.items} onItemClick={onItemClick} actions={actions} />
          )}
          {data.type === 'service-logs' && (
            <ServiceLogsTable items={data.items} onItemClick={onItemClick} actions={actions} />
          )}
          {data.type === 'routes' && (
            <RoutesTable items={data.items} />
          )}
          {data.type === 'aging-clients' && (
            <AgingClientsTable items={data.items} onItemClick={onItemClick} />
          )}
          {data.type === 'missing-data-clients' && (
            <MissingDataClientsTable items={data.items} onItemClick={onItemClick} actions={actions} />
          )}
          {data.type === 'suspicious-data-clients' && (
            <SuspiciousDataClientsTable items={data.items} onItemClick={onItemClick} actions={actions} />
          )}
          {data.type === 'suspicious-locations' && (
            <SuspiciousLocationsTable
              items={data.items}
              onDismiss={actions?.onDismissLocationAlert}
            />
          )}
          {data.type === 'inventory-review' && (
            <InventoryReviewTable items={data.items} onItemClick={onItemClick} />
          )}
        </div>
      </div>
    </div>
  );
}

function TableHeader({
  title,
  description,
  count,
  activeCount,
  dismissedCount,
}: {
  title: string;
  description: string;
  count?: number;
  activeCount?: number;
  dismissedCount?: number;
}) {
  // If we have dual counts, show them; otherwise fall back to total count
  const hasDualCounts = activeCount !== undefined && dismissedCount !== undefined;

  return (
    <div className="px-6 py-4 border-b border-sage-100 bg-sage-50">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display font-semibold text-forest-900">{title}</h3>
            {hasDualCounts ? (
              <div className="flex items-center gap-1">
                <Badge variant={activeCount > 0 ? 'warning' : 'success'} size="sm">
                  {activeCount} active
                </Badge>
                {dismissedCount > 0 && (
                  <span className="text-xs text-sage-400">/ {dismissedCount} dismissed</span>
                )}
              </div>
            ) : count !== undefined && (
              <Badge variant="default" size="sm">
                {count} items
              </Badge>
            )}
          </div>
          <p className="text-sm text-sage-600 mt-0.5">{description}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Keyboard shortcuts hint - desktop only */}
          <div className="hidden lg:flex items-center gap-1.5 text-xs text-sage-400">
            <kbd className="px-1.5 py-0.5 bg-sage-100 rounded text-sage-500 font-mono">↑↓</kbd>
            <span>navigate</span>
            <kbd className="px-1.5 py-0.5 bg-sage-100 rounded text-sage-500 font-mono ml-1">↵</kbd>
            <span>open</span>
          </div>
          <Button variant="ghost" size="sm">
            Export
          </Button>
        </div>
      </div>
    </div>
  );
}

// Workorders table
function WorkordersTable({
  items,
  onItemClick,
  actions,
  alertType,
}: {
  items: WorkOrder[];
  onItemClick?: (item: WorkOrder) => void;
  actions?: AlertItemActions;
  alertType?: AlertType | null;
}) {
  const [activeMenu, setActiveMenu] = useState<number | null>(null);

  // Custom multi-level sort for Ready for Action (per Item 3):
  // 1. Urgent flag (urgents at top)
  // 2. Assigned To: Unassigned first, then alpha by name
  // 3. Area code (alphabetical)
  // 4. Client Name (alphabetical)
  // 5. Age (descending - oldest first)
  const sortedForReadyForAction = useMemo(() => {
    if (alertType !== 'wo-ready-pickup') return null;

    return [...items].sort((a, b) => {
      // 1. Urgent first
      if (a.urgent !== b.urgent) {
        return a.urgent ? -1 : 1;
      }

      // 2. Unassigned first, then alpha by assignee name
      const aUnassigned = !a.assignedToName;
      const bUnassigned = !b.assignedToName;
      if (aUnassigned !== bUnassigned) {
        return aUnassigned ? -1 : 1;
      }
      if (!aUnassigned && a.assignedToName && b.assignedToName) {
        const nameCompare = a.assignedToName.localeCompare(b.assignedToName);
        if (nameCompare !== 0) return nameCompare;
      }

      // 3. Area code (alphabetical, items without area code sort to end)
      const aAreaCode = a.clientAreaCode || '';
      const bAreaCode = b.clientAreaCode || '';
      if (aAreaCode !== bAreaCode) {
        // Empty area codes go to the end
        if (!aAreaCode) return 1;
        if (!bAreaCode) return -1;
        const areaCompare = aAreaCode.localeCompare(bAreaCode);
        if (areaCompare !== 0) return areaCompare;
      }

      // 4. Client name (alphabetical)
      const clientCompare = (a.clientName || '').localeCompare(b.clientName || '');
      if (clientCompare !== 0) return clientCompare;

      // 5. Age descending (older first)
      const aAge = a.stalledDays ?? Math.floor((Date.now() - new Date(a.timestamp).getTime()) / (1000 * 60 * 60 * 24));
      const bAge = b.stalledDays ?? Math.floor((Date.now() - new Date(b.timestamp).getTime()) / (1000 * 60 * 60 * 24));
      return bAge - aAge; // descending
    });
  }, [items, alertType]);

  // Sortable table (used for non-Ready-for-Action views)
  const { sortedData: tableSortedData, sortConfig, requestSort, getSortDirection } = useTableSort(items, {
    initialSort: { key: 'timestamp', direction: 'desc' },
    comparators: {
      timestamp: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      stalledDays: (a, b) => (a.stalledDays ?? 0) - (b.stalledDays ?? 0),
    },
  });

  // Use custom default sort for Ready for Action unless user has explicitly clicked a column header.
  // "No user sort" = initial state (key='timestamp') or user has reset by cycling back (key='').
  // Any other key means a visible column header was clicked.
  const isDefaultSort = sortConfig.key === '' || sortConfig.key === 'timestamp';
  const sortedData = (sortedForReadyForAction && isDefaultSort) ? sortedForReadyForAction : tableSortedData;

  // Keyboard navigation
  const {
    focusedIndex,
    handleKeyDown,
    containerRef,
    setFocusedIndex,
  } = useKeyboardNavigation({
    itemCount: sortedData.length,
    onSelect: (index) => onItemClick?.(sortedData[index]),
  });

  // Scroll focused row into view
  useEffect(() => {
    if (focusedIndex >= 0 && containerRef.current) {
      const rows = containerRef.current.querySelectorAll('tbody tr');
      rows[focusedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex, containerRef]);

  // Calculate age in days
  const getAgeDays = (timestamp: string) => {
    const created = new Date(timestamp);
    const now = new Date();
    return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Get status badge variant
  const getStatusVariant = (status: string): 'default' | 'success' | 'warning' | 'danger' | 'info' => {
    if (status.startsWith('1.')) return 'warning';
    if (status.startsWith('2.') || status.startsWith('3.')) return 'info';
    if (status.startsWith('4.')) return 'default';
    if (status.startsWith('5.') || status.startsWith('6.')) return 'success';
    if (status.startsWith('7.')) return 'danger';
    return 'default';
  };

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
    setActiveMenu(null);
  };

  return (
    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className="outline-none"
    >
      <table className="w-full">
        <thead className="bg-sage-50 sticky top-0">
        <tr>
          <SortableTableHeader columnKey="detailedDescription" label="Work Order" sortDirection={getSortDirection('detailedDescription')} onSort={requestSort} />
          <SortableTableHeader columnKey="clientName" label="Client" sortDirection={getSortDirection('clientName')} onSort={requestSort} />
          <SortableTableHeader columnKey="assignedToName" label="Assigned To" sortDirection={getSortDirection('assignedToName')} onSort={requestSort} />
          <SortableTableHeader columnKey="status" label="Status" sortDirection={getSortDirection('status')} onSort={requestSort} />
          <SortableTableHeader columnKey="stalledDays" label="Age" sortDirection={getSortDirection('stalledDays')} onSort={requestSort} />
          <th className="px-6 py-3 text-left text-xs font-medium text-sage-600 uppercase tracking-wider w-32">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-sage-100">
        {sortedData.map((wo, index) => (
          <tr
            key={wo.id}
            onClick={() => onItemClick?.(wo)}
            onMouseEnter={() => setFocusedIndex(index)}
            className={`cursor-pointer transition-colors group ${
              focusedIndex === index
                ? 'bg-primary-50 ring-2 ring-inset ring-primary-300'
                : 'hover:bg-sage-50'
            }`}
          >
            <td className="px-6 py-4">
              <div className="flex items-center gap-2">
                {wo.urgent && (
                  <span className="text-red-500" title="Urgent">🚨</span>
                )}
                <div>
                  <p className="font-medium text-forest-900 line-clamp-1">
                    {wo.detailedDescription || `${wo.qty || 1} ${wo.itemName || 'Item'}`}
                  </p>
                  {wo.notes && (
                    <p className="text-xs text-sage-500 line-clamp-1 mt-0.5">{wo.notes}</p>
                  )}
                </div>
              </div>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-forest-700">{wo.clientName || 'Unknown'}</p>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-forest-700">
                {wo.assignedToName || (
                  <span className="text-red-600 font-medium">Unassigned</span>
                )}
              </p>
            </td>
            <td className="px-6 py-4">
              <Badge variant={getStatusVariant(wo.status)} size="sm">
                {wo.status.split('.')[1]?.trim() || wo.status}
              </Badge>
            </td>
            <td className="px-6 py-4">
              {wo.stalledDays !== undefined ? (
                <div>
                  <Badge
                    variant={wo.stalledDays > (wo.thresholdDays || 7) * 2 ? 'danger' : 'warning'}
                    size="sm"
                  >
                    {wo.stalledDays}d stalled
                  </Badge>
                  {wo.thresholdDays && (
                    <p className="text-xs text-sage-500 mt-0.5">
                      {wo.thresholdDays}d threshold
                    </p>
                  )}
                </div>
              ) : (
                <span className="text-sm text-sage-600">{getAgeDays(wo.timestamp)}d</span>
              )}
            </td>
            <td className="px-6 py-4">
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {actions?.onEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 text-sage-500 hover:text-forest-700"
                    onClick={(e) => handleAction(e, () => actions.onEdit?.(wo))}
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Button>
                )}
                {actions?.onAssign && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 text-sage-500 hover:text-forest-700"
                    onClick={(e) => handleAction(e, () => actions.onAssign?.(wo))}
                    title="Assign"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </Button>
                )}
                {actions?.onAddNote && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 text-sage-500 hover:text-forest-700"
                    onClick={(e) => handleAction(e, () => actions.onAddNote?.(wo))}
                    title="Add Note"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </Button>
                )}
                {/* Status quick-change dropdown */}
                {actions?.onStatusChange && (
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1.5 text-sage-500 hover:text-forest-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === wo.id ? null : wo.id);
                      }}
                      title="Change Status"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </Button>
                    {activeMenu === wo.id && (
                      <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-sage-200 py-1 w-40">
                        {[
                          '2. In Progress',
                          '3. Assigned',
                          '4. Ordered',
                          '5. Complete',
                          '6. Ready for Pickup',
                        ].map((status) => (
                          <button
                            key={status}
                            onClick={(e) => handleAction(e, () => actions.onStatusChange?.(wo, status))}
                            className="w-full px-3 py-1.5 text-left text-sm text-forest-700 hover:bg-sage-50"
                          >
                            {status.split('.')[1]?.trim()}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1.5 text-sage-400 hover:text-forest-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemClick?.(wo);
                  }}
                  title="View Details"
                >
                  <span>→</span>
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

// Clients table
function ClientsTable({
  items,
  onItemClick,
  actions,
}: {
  items: Client[];
  onItemClick?: (item: Client) => void;
  actions?: AlertItemActions;
}) {
  // Format address for display
  const formatAddress = (address: Client['address']) => {
    if (!address) return 'No address';
    const parts = [address.city, address.state].filter(Boolean);
    return parts.join(', ') || 'No address';
  };

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <table className="w-full">
      <thead className="bg-sage-50 sticky top-0">
        <tr className="text-left text-xs font-medium text-sage-600 uppercase tracking-wider">
          <th className="px-6 py-3">Client</th>
          <th className="px-6 py-3">Location</th>
          <th className="px-6 py-3">Area</th>
          <th className="px-6 py-3">Monthly</th>
          <th className="px-6 py-3 w-24">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-sage-100">
        {items.map((client) => (
          <tr
            key={client.id}
            onClick={() => onItemClick?.(client)}
            className="hover:bg-sage-50 cursor-pointer transition-colors group"
          >
            <td className="px-6 py-4">
              <div>
                <p className="font-medium text-forest-900">{client.clientName}</p>
                {client.businessName && (
                  <p className="text-xs text-sage-500">{client.businessName}</p>
                )}
              </div>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-forest-700">{formatAddress(client.address)}</p>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-forest-700">{client.areaCode || '—'}</p>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-forest-700">
                {client.monthlyAmount ? `$${client.monthlyAmount}` : '—'}
              </p>
            </td>
            <td className="px-6 py-4">
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {actions?.onEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 text-sage-500 hover:text-forest-700"
                    onClick={(e) => handleAction(e, () => actions.onEdit?.(client))}
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Button>
                )}
                {actions?.onAddNote && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 text-sage-500 hover:text-forest-700"
                    onClick={(e) => handleAction(e, () => actions.onAddNote?.(client))}
                    title="Add Note"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1.5 text-sage-400 hover:text-forest-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemClick?.(client);
                  }}
                  title="View Details"
                >
                  <span>→</span>
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Service Logs table (with gap analysis for missed services)
function ServiceLogsTable({
  items,
  onItemClick,
  actions,
}: {
  items: ServiceLog[];
  onItemClick?: (item: ServiceLog) => void;
  actions?: AlertItemActions;
}) {
  // Format time from timestamp
  const formatTime = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '—';
    }
  };

  // Format date for display
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Get gap severity variant
  const getGapVariant = (gapRatio: number | null | undefined): 'success' | 'warning' | 'danger' | 'default' => {
    if (!gapRatio) return 'default';
    if (gapRatio >= 2) return 'danger';
    if (gapRatio >= 1.5) return 'warning';
    return 'success';
  };

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  // Check if any items have gap analysis data
  const hasGapData = items.some(log => log.intervalDays !== undefined);

  return (
    <table className="w-full">
      <thead className="bg-sage-50 sticky top-0">
        <tr className="text-left text-xs font-medium text-sage-600 uppercase tracking-wider">
          <th className="px-6 py-3">Client</th>
          <th className="px-6 py-3">Tech</th>
          <th className="px-6 py-3">Type</th>
          <th className="px-6 py-3">Missed On</th>
          {hasGapData && (
            <>
              <th className="px-6 py-3">Last Complete</th>
              <th className="px-6 py-3">Next Scheduled</th>
              <th className="px-6 py-3">Gap</th>
            </>
          )}
          <th className="px-6 py-3 w-20">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-sage-100">
        {items.map((log) => (
          <tr
            key={log.id}
            onClick={() => onItemClick?.(log)}
            className="hover:bg-sage-50 cursor-pointer transition-colors group"
          >
            <td className="px-6 py-4">
              <div>
                <p className="font-medium text-forest-900">{log.clientName || 'Unknown'}</p>
                {log.serviceNotes && (
                  <p className="text-xs text-sage-500 line-clamp-1 mt-0.5">{log.serviceNotes}</p>
                )}
              </div>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-forest-700">{log.employeeName || 'Unknown'}</p>
            </td>
            <td className="px-6 py-4">
              <Badge
                variant={log.missedService === 'Partial Missed' ? 'warning' : 'danger'}
                size="sm"
              >
                {log.missedService || 'Missed'}
              </Badge>
            </td>
            <td className="px-6 py-4">
              <div>
                <p className="text-sm text-forest-700">{formatDate(log.date)}</p>
                <p className="text-xs text-sage-500">{formatTime(log.timestamp)}</p>
              </div>
            </td>
            {hasGapData && (
              <>
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm text-forest-700">
                      {log.lastCompletedVisit ? formatDate(log.lastCompletedVisit) : 'Never'}
                    </p>
                    {log.daysSinceLastComplete !== null && log.daysSinceLastComplete !== undefined && (
                      <p className="text-xs text-sage-500">{log.daysSinceLastComplete}d ago</p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm text-forest-700">
                      {log.nextScheduledDate ? formatDate(log.nextScheduledDate) : 'Not scheduled'}
                    </p>
                    {log.daysUntilNextVisit !== null && log.daysUntilNextVisit !== undefined && (
                      <p className="text-xs text-sage-500">in {log.daysUntilNextVisit}d</p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    {log.gapVsInterval !== null && log.gapVsInterval !== undefined ? (
                      <>
                        <Badge variant={getGapVariant(log.gapVsInterval)} size="sm">
                          {log.gapVsInterval}x interval
                        </Badge>
                        {log.intervalDays && (
                          <p className="text-xs text-sage-500 mt-0.5">{log.intervalDays}d expected</p>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-sage-400">—</span>
                    )}
                  </div>
                </td>
              </>
            )}
            <td className="px-6 py-4">
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {actions?.onAddNote && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 text-sage-500 hover:text-forest-700"
                    onClick={(e) => handleAction(e, () => actions.onAddNote?.(log))}
                    title="Add Note"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1.5 text-sage-400 hover:text-forest-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemClick?.(log);
                  }}
                  title="View Details"
                >
                  <span>→</span>
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Routes table (for Route Not Scheduled alert)
function RoutesTable({
  items,
}: {
  items: RouteAlert[];
}) {
  return (
    <table className="w-full">
      <thead className="bg-sage-50 sticky top-0">
        <tr className="text-left text-xs font-medium text-sage-600 uppercase tracking-wider">
          <th className="px-6 py-3">Route Code</th>
          <th className="px-6 py-3">Assigned Tech</th>
          <th className="px-6 py-3">Stops</th>
          <th className="px-6 py-3">Duration</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-sage-100">
        {items.map((route) => (
          <tr
            key={route.id}
            className="hover:bg-sage-50 transition-colors"
          >
            <td className="px-6 py-4">
              <div className="flex items-center gap-2">
                <span className="text-amber-500" title="Not Scheduled">📅</span>
                <p className="font-medium text-forest-900 font-mono">{route.routeCode}</p>
              </div>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-forest-700">{route.employeeName || 'Unassigned'}</p>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-sage-600">{route.stopCount || 0} stops</p>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-sage-600">
                {route.totalDuration ? `${Math.round(route.totalDuration / 60)}h ${route.totalDuration % 60}m` : '—'}
              </p>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Aging Clients table
function AgingClientsTable({
  items,
  onItemClick,
  actions,
}: {
  items: AgingClient[];
  onItemClick?: (item: AgingClient) => void;
  actions?: AlertItemActions;
}) {
  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  // Get severity badge variant
  const getSeverityVariant = (severity: string): 'warning' | 'danger' | 'earth' | 'default' => {
    switch (severity) {
      case 'critical':
        return 'danger';
      case 'danger':
        return 'earth';
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Separate active and dismissed items
  const activeItemsUnsorted = items.filter(item => !item.isDismissed);
  const dismissedItems = items.filter(item => item.isDismissed);

  // Sortable table for active items
  const { sortedData: activeItems, requestSort, getSortDirection } = useTableSort(activeItemsUnsorted, {
    initialSort: { key: 'daysSinceService', direction: 'desc' },
    comparators: {
      severity: (a, b) => {
        const order = { critical: 3, danger: 2, warning: 1 };
        return (order[a.severity as keyof typeof order] ?? 0) - (order[b.severity as keyof typeof order] ?? 0);
      },
    },
  });

  // Keyboard navigation
  const {
    focusedIndex,
    handleKeyDown,
    containerRef,
    setFocusedIndex,
  } = useKeyboardNavigation({
    itemCount: activeItems.length,
    onSelect: (index) => onItemClick?.(activeItems[index]),
  });

  // Scroll focused row into view
  useEffect(() => {
    if (focusedIndex >= 0 && containerRef.current) {
      const rows = containerRef.current.querySelectorAll('tbody tr');
      rows[focusedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex, containerRef]);

  // Render a single row (used for both active and dismissed items)
  const renderClientRow = (client: AgingClient, _index: number, isDismissedRow = false) => (
    <>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          {client.severity === 'critical' && (
            <span className="text-red-500" title="Critical">🚨</span>
          )}
          <div>
            <p className="font-medium text-forest-900">{client.clientName}</p>
            {client.businessName && (
              <p className="text-xs text-sage-500">{client.businessName}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-forest-700 font-mono">{client.routeId || '—'}</p>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-sage-600">{client.intervalDays}d expected</p>
      </td>
      <td className="px-6 py-4">
        <div>
          <p className="text-sm text-forest-700">{client.lastServiceDate || 'Never'}</p>
          {client.lastServiceTech && (
            <p className="text-xs text-sage-500">by {client.lastServiceTech}</p>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-forest-700">
          {client.daysSinceService === 9999 ? 'Never serviced' : `${client.daysSinceService}d ago`}
        </p>
        <p className="text-xs text-sage-500">
          {client.multiplier === 999 ? '—' : `${client.multiplier}x interval`}
        </p>
      </td>
      <td className="px-6 py-4">
        <Badge variant={getSeverityVariant(client.severity)} size="sm">
          {client.severity}
        </Badge>
      </td>
      {!isDismissedRow && (
        <td className="px-6 py-4">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {actions?.onDismissAlert && !client.isDismissed && (
              <Button
                variant="ghost"
                size="sm"
                className="p-1.5 text-sage-500 hover:text-sage-700"
                onClick={(e) => handleAction(e, () => actions.onDismissAlert?.(client, 'aging'))}
                title="Dismiss alert"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="p-1.5 text-sage-400 hover:text-forest-700"
              onClick={(e) => {
                e.stopPropagation();
                onItemClick?.(client);
              }}
              title="View Details"
            >
              <span>→</span>
            </Button>
          </div>
        </td>
      )}
    </>
  );

  return (
    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className="outline-none"
    >
    <table className="w-full">
      <thead className="bg-sage-50 sticky top-0">
        <tr>
          <SortableTableHeader columnKey="clientName" label="Client" sortDirection={getSortDirection('clientName')} onSort={requestSort} />
          <SortableTableHeader columnKey="routeId" label="Route" sortDirection={getSortDirection('routeId')} onSort={requestSort} />
          <SortableTableHeader columnKey="intervalDays" label="Interval" sortDirection={getSortDirection('intervalDays')} onSort={requestSort} />
          <SortableTableHeader columnKey="lastServiceDate" label="Last Service" sortDirection={getSortDirection('lastServiceDate')} onSort={requestSort} />
          <SortableTableHeader columnKey="daysSinceService" label="Days Overdue" sortDirection={getSortDirection('daysSinceService')} onSort={requestSort} />
          <SortableTableHeader columnKey="severity" label="Severity" sortDirection={getSortDirection('severity')} onSort={requestSort} />
          <th className="px-6 py-3 text-left text-xs font-medium text-sage-600 uppercase tracking-wider w-32">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-sage-100">
        {activeItems.map((client, index) => (
          <tr
            key={client.id}
            onClick={() => onItemClick?.(client)}
            onMouseEnter={() => setFocusedIndex(index)}
            className={`cursor-pointer transition-colors group ${
              focusedIndex === index
                ? 'bg-primary-50 ring-2 ring-inset ring-primary-300'
                : 'hover:bg-sage-50'
            }`}
          >
            {renderClientRow(client, index)}
          </tr>
        ))}
      </tbody>
    </table>

    {/* Dismissed items section */}
    <DismissedItemsSection
      items={dismissedItems}
      onUndo={actions?.onUndoDismissal}
      renderRow={(item, idx) => renderClientRow(item, idx, true)}
    />
    </div>
  );
}

// Missing Data Clients table
function MissingDataClientsTable({
  items,
  onItemClick,
  actions,
}: {
  items: MissingDataClient[];
  onItemClick?: (item: MissingDataClient) => void;
  actions?: AlertItemActions;
}) {
  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  // Separate active and dismissed items
  const activeItemsUnsorted = items.filter(item => !item.isDismissed);
  const dismissedItems = items.filter(item => item.isDismissed);

  // Sortable table for active items
  const { sortedData: activeItems, requestSort, getSortDirection } = useTableSort(activeItemsUnsorted, {
    initialSort: { key: 'missingFieldCount', direction: 'desc' },
    comparators: {
      missingFieldCount: (a, b) => a.missingFields.length - b.missingFields.length,
    },
  });

  // Keyboard navigation
  const {
    focusedIndex,
    handleKeyDown,
    containerRef,
    setFocusedIndex,
  } = useKeyboardNavigation({
    itemCount: activeItems.length,
    onSelect: (index) => onItemClick?.(activeItems[index]),
  });

  // Scroll focused row into view
  useEffect(() => {
    if (focusedIndex >= 0 && containerRef.current) {
      const rows = containerRef.current.querySelectorAll('tbody tr');
      rows[focusedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex, containerRef]);

  // Render a single row (used for both active and dismissed items)
  const renderClientRow = (client: MissingDataClient, _index: number, isDismissedRow = false) => (
    <>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="text-amber-500" title="Missing Data">📋</span>
          <div>
            <p className="font-medium text-forest-900">{client.clientName}</p>
            {client.businessName && (
              <p className="text-xs text-sage-500">{client.businessName}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-forest-700 font-mono">{client.routeId || '—'}</p>
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {client.missingFields.slice(0, 4).map((field) => (
            <Badge key={field} variant="warning" size="sm">
              {field}
            </Badge>
          ))}
          {client.missingFields.length > 4 && (
            <Badge variant="default" size="sm">
              +{client.missingFields.length - 4} more
            </Badge>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <Badge
          variant={client.missingFields.length >= 5 ? 'danger' : client.missingFields.length >= 3 ? 'warning' : 'default'}
          size="sm"
        >
          {client.missingFields.length} fields
        </Badge>
      </td>
      {!isDismissedRow && (
        <td className="px-6 py-4">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {actions?.onFixMissingData && (
              <Button
                variant="ghost"
                size="sm"
                className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                onClick={(e) => handleAction(e, () => actions.onFixMissingData?.(client))}
                title="Fix missing data"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Button>
            )}
            {actions?.onDismissAlert && !client.isDismissed && (
              <Button
                variant="ghost"
                size="sm"
                className="p-1.5 text-sage-500 hover:text-sage-700"
                onClick={(e) => handleAction(e, () => actions.onDismissAlert?.(client, 'missing_data'))}
                title="Dismiss alert"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="p-1.5 text-sage-400 hover:text-forest-700"
              onClick={(e) => {
                e.stopPropagation();
                onItemClick?.(client);
              }}
              title="View Details"
            >
              <span>→</span>
            </Button>
          </div>
        </td>
      )}
    </>
  );

  return (
    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className="outline-none"
    >
      <table className="w-full">
        <thead className="bg-sage-50 sticky top-0">
          <tr>
            <SortableTableHeader columnKey="clientName" label="Client" sortDirection={getSortDirection('clientName')} onSort={requestSort} />
            <SortableTableHeader columnKey="routeId" label="Route" sortDirection={getSortDirection('routeId')} onSort={requestSort} />
            <th className="px-6 py-3 text-left text-xs font-medium text-sage-600 uppercase tracking-wider">Missing Fields</th>
            <SortableTableHeader columnKey="missingFieldCount" label="Count" sortDirection={getSortDirection('missingFieldCount')} onSort={requestSort} />
            <th className="px-6 py-3 text-left text-xs font-medium text-sage-600 uppercase tracking-wider w-32">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-sage-100">
          {activeItems.map((client, index) => (
            <tr
              key={client.id}
              onClick={() => onItemClick?.(client)}
              onMouseEnter={() => setFocusedIndex(index)}
              className={`cursor-pointer transition-colors group ${
                focusedIndex === index
                  ? 'bg-primary-50 ring-2 ring-inset ring-primary-300'
                  : 'hover:bg-sage-50'
              }`}
            >
              {renderClientRow(client, index)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Dismissed items section */}
      <DismissedItemsSection
        items={dismissedItems}
        onUndo={actions?.onUndoDismissal}
        renderRow={(item, idx) => renderClientRow(item, idx, true)}
      />
    </div>
  );
}

// Suspicious Data Clients table
function SuspiciousDataClientsTable({
  items,
  onItemClick,
  actions,
}: {
  items: SuspiciousDataClient[];
  onItemClick?: (item: SuspiciousDataClient) => void;
  actions?: AlertItemActions;
}) {
  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  // Separate active and dismissed items
  const activeItemsUnsorted = items.filter(item => !item.isDismissed);
  const dismissedItems = items.filter(item => item.isDismissed);

  // Sortable table for active items
  const { sortedData: activeItems, requestSort, getSortDirection } = useTableSort(activeItemsUnsorted, {
    initialSort: { key: 'clientName', direction: 'asc' },
  });

  // Keyboard navigation
  const {
    focusedIndex,
    handleKeyDown,
    containerRef,
    setFocusedIndex,
  } = useKeyboardNavigation({
    itemCount: activeItems.length,
    onSelect: (index) => onItemClick?.(activeItems[index]),
  });

  // Scroll focused row into view
  useEffect(() => {
    if (focusedIndex >= 0 && containerRef.current) {
      const rows = containerRef.current.querySelectorAll('tbody tr');
      rows[focusedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex, containerRef]);

  // Get human-readable issue type label
  const getIssueLabel = (issueType: string): string => {
    switch (issueType) {
      case 'missing_geocode':
        return 'Missing Geocode';
      case 'geocode_outlier':
        return 'Bad Location';
      case 'invalid_interval':
        return 'Invalid Interval';
      case 'duplicate_name':
        return 'Duplicate Name';
      case 'tech_mismatch':
        return 'Tech Mismatch';
      default:
        return issueType;
    }
  };

  // Get issue icon
  const getIssueIcon = (issueType: string): string => {
    switch (issueType) {
      case 'missing_geocode':
        return '📍';
      case 'geocode_outlier':
        return '🗺️';
      case 'invalid_interval':
        return '⏱️';
      case 'duplicate_name':
        return '👥';
      case 'tech_mismatch':
        return '🔄';
      default:
        return '🔍';
    }
  };

  // Get badge variant based on issue type
  const getIssueVariant = (issueType: string): 'warning' | 'danger' | 'info' | 'default' => {
    switch (issueType) {
      case 'missing_geocode':
        return 'warning';
      case 'geocode_outlier':
        return 'danger';
      case 'invalid_interval':
        return 'warning';
      case 'duplicate_name':
        return 'info';
      case 'tech_mismatch':
        return 'info';
      default:
        return 'default';
    }
  };

  // Check if this issue type has a fix action
  const canFix = (issueType: string): boolean => {
    return ['missing_geocode', 'geocode_outlier', 'invalid_interval'].includes(issueType);
  };

  // Render a single row (used for both active and dismissed items)
  const renderClientRow = (client: SuspiciousDataClient, _index: number, isDismissedRow = false) => (
    <>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span title={getIssueLabel(client.issueType)}>{getIssueIcon(client.issueType)}</span>
          <div>
            <p className="font-medium text-forest-900">{client.clientName}</p>
            {client.businessName && (
              <p className="text-xs text-sage-500">{client.businessName}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-forest-700 font-mono">{client.routeId || '—'}</p>
      </td>
      <td className="px-6 py-4">
        <Badge variant={getIssueVariant(client.issueType)} size="sm">
          {getIssueLabel(client.issueType)}
        </Badge>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-sage-600 line-clamp-2">
          {client.issueDetail || '—'}
        </p>
      </td>
      {!isDismissedRow && (
        <td className="px-6 py-4">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Fix button - varies by issue type */}
            {canFix(client.issueType) && (
              <>
                {(client.issueType === 'missing_geocode' || client.issueType === 'geocode_outlier') && actions?.onFixLocation && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                    onClick={(e) => handleAction(e, () => actions.onFixLocation?.(client))}
                    title="Fix location"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </Button>
                )}
                {client.issueType === 'invalid_interval' && actions?.onFixInterval && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                    onClick={(e) => handleAction(e, () => actions.onFixInterval?.(client))}
                    title="Fix interval"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Button>
                )}
              </>
            )}
            {/* Dismiss button */}
            {actions?.onDismissAlert && !client.isDismissed && (
              <Button
                variant="ghost"
                size="sm"
                className="p-1.5 text-sage-500 hover:text-sage-700"
                onClick={(e) => handleAction(e, () => actions.onDismissAlert?.(client, client.issueType))}
                title="Dismiss alert"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="p-1.5 text-sage-400 hover:text-forest-700"
              onClick={(e) => {
                e.stopPropagation();
                onItemClick?.(client);
              }}
              title="View Details"
            >
              <span>→</span>
            </Button>
          </div>
        </td>
      )}
    </>
  );

  return (
    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className="outline-none"
    >
      <table className="w-full">
        <thead className="bg-sage-50 sticky top-0">
          <tr>
            <SortableTableHeader columnKey="clientName" label="Client" sortDirection={getSortDirection('clientName')} onSort={requestSort} />
            <SortableTableHeader columnKey="routeId" label="Route" sortDirection={getSortDirection('routeId')} onSort={requestSort} />
            <SortableTableHeader columnKey="issueType" label="Issue Type" sortDirection={getSortDirection('issueType')} onSort={requestSort} />
            <th className="px-6 py-3 text-left text-xs font-medium text-sage-600 uppercase tracking-wider">Details</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-sage-600 uppercase tracking-wider w-32">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-sage-100">
          {activeItems.map((client, index) => (
            <tr
              key={`${client.id}-${client.issueType}-${index}`}
              onClick={() => onItemClick?.(client)}
              onMouseEnter={() => setFocusedIndex(index)}
              className={`cursor-pointer transition-colors group ${
                focusedIndex === index
                  ? 'bg-primary-50 ring-2 ring-inset ring-primary-300'
                  : 'hover:bg-sage-50'
              }`}
            >
              {renderClientRow(client, index)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Dismissed items section */}
      <DismissedItemsSection
        items={dismissedItems}
        onUndo={actions?.onUndoDismissal}
        renderRow={(item, idx) => renderClientRow(item, idx, true)}
      />
    </div>
  );
}

// Inventory Review table (pending billing acknowledgement)
function InventoryReviewTable({
  items,
  onItemClick,
}: {
  items: PendingInventoryReview[];
  onItemClick?: (item: PendingInventoryReview) => void;
}) {
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatPricingImpact = (impact: number) => {
    if (impact === 0) return '—';
    const sign = impact > 0 ? '+' : '';
    return `${sign}$${Math.abs(impact).toFixed(0)}/mo`;
  };

  return (
    <table className="w-full">
      <thead className="bg-sage-50 sticky top-0">
        <tr className="text-left text-xs font-medium text-sage-600 uppercase tracking-wider">
          <th className="px-6 py-3">Client</th>
          <th className="px-6 py-3">Changes</th>
          <th className="px-6 py-3">Pricing Impact</th>
          <th className="px-6 py-3">Oldest Change</th>
          <th className="px-6 py-3 w-20">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-sage-100">
        {items.map((review) => (
          <tr
            key={review.clientId}
            onClick={() => onItemClick?.(review)}
            className="hover:bg-sage-50 cursor-pointer transition-colors group"
          >
            <td className="px-6 py-4">
              <div className="flex items-center gap-2">
                <span title="Inventory Review">📦</span>
                <p className="font-medium text-forest-900">{review.clientName || 'Unknown Client'}</p>
              </div>
            </td>
            <td className="px-6 py-4">
              <Badge variant="warning" size="sm">
                {review.changeCount} {review.changeCount === 1 ? 'change' : 'changes'}
              </Badge>
            </td>
            <td className="px-6 py-4">
              <span className={`text-sm font-medium ${
                review.netPricingImpact > 0 ? 'text-primary-700' :
                review.netPricingImpact < 0 ? 'text-red-600' :
                'text-sage-500'
              }`}>
                {formatPricingImpact(review.netPricingImpact)}
              </span>
            </td>
            <td className="px-6 py-4">
              <p className="text-sm text-sage-600">{formatDate(review.oldestChangeAt)}</p>
            </td>
            <td className="px-6 py-4">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1.5 text-sage-400 hover:text-forest-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemClick?.(review);
                  }}
                  title="Review Changes"
                >
                  <span>→</span>
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// =============================================================================
// DismissedItemsSection - Collapsible section for dismissed items (F4 Phase 2)
// =============================================================================

interface DismissedItemsSectionProps<T> {
  items: T[];
  onUndo?: (dismissalId: number) => void;
  renderRow: (item: T, index: number) => React.ReactNode;
}

// Exported for use in MissingDataClientsTable, SuspiciousDataClientsTable, and other tables
export function DismissedItemsSection<T extends {
  dismissalId?: number;
  dismissedAt?: string;
  dismissedByName?: string;
  reasonCode?: string;
  reasonNote?: string;
  dismissalType?: string;
}>({
  items,
  onUndo,
  renderRow,
}: DismissedItemsSectionProps<T>) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (items.length === 0) return null;

  // Format relative time
  const formatRelativeTime = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  // Format reason code for display
  const formatReasonCode = (code: string | undefined) => {
    if (!code) return 'No reason specified';
    return code
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Build tooltip text
  const buildTooltip = (item: T) => {
    const reason = formatReasonCode(item.reasonCode);
    const note = item.reasonNote ? `\n"${item.reasonNote}"` : '';
    const type = item.dismissalType === 'ongoing' ? '\n(Ongoing exception)' : '';
    return `${reason}${note}${type}`;
  };

  return (
    <div className="border-t border-sage-200">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-3 flex items-center justify-between text-left hover:bg-sage-50/50 transition-colors"
      >
        <span className="text-sm font-medium text-sage-500">
          Dismissed ({items.length})
        </span>
        <svg
          className={`w-4 h-4 text-sage-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="bg-sage-50/30">
          <table className="w-full">
            <tbody className="divide-y divide-sage-100">
              {items.map((item, index) => (
                <tr
                  key={index}
                  className="opacity-60 hover:opacity-80 transition-opacity group"
                >
                  {renderRow(item, index)}
                  {/* Undo button cell */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="text-xs text-sage-400 cursor-help flex items-center gap-1"
                        title={buildTooltip(item)}
                      >
                        {item.dismissedByName && <span>{item.dismissedByName}</span>}
                        {item.dismissedAt && <span> • {formatRelativeTime(item.dismissedAt)}</span>}
                        {(item.reasonCode || item.reasonNote) && (
                          <svg
                            className="w-3.5 h-3.5 text-sage-300 hover:text-sage-500 transition-colors inline-block ml-0.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      {onUndo && item.dismissalId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 text-xs text-primary-600 hover:text-primary-700 opacity-0 group-hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUndo(item.dismissalId!);
                          }}
                          title="Undo dismissal"
                        >
                          Undo
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
